import { ChangeEvent, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Grid, Link, Tab, Tabs, Tooltip, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  GridColDef,
  GridRenderCellParams,
  GridToolbarFilterButton,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { truncateText } from 'helpers';
import {
  EstateTask_FieldsFragment,
  EstateTask_MasterTaskListDataGridPageFilter,
  EstateTaskDeadlineType,
  EstateTaskGroup,
  EstateType,
  MasterTaskListDataGridColumns,
  useEstateTask_GetAllEstateTasksForMasterTaskListDataGridLazyQuery,
} from 'queries';
import { ChevronRight, Icon } from 'ts-react-feather-icons';

import { ServerEnabledDataGrid } from 'common/server-enabled-data-grid';
import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import { EstateTaskGroupLabel } from 'utils/estate';
import { GridStateKey } from 'utils/grid-state';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import { defaultPaginationModel } from 'utils/pagination-model';

import CreateEstateTaskModal from './components/create-estate-task-modal';
import UpdateEstateTaskModal from './components/update-estate-task-modal';

const useStyles = makeStyles(() => ({
  root: {
    // no border around the cells when focused
    '&.MuiDataGrid-root .MuiDataGrid-cell, &.MuiDataGrid-root .MuiDataGrid-columnHeader': {
      '&:focus, &:focus-within': {
        outline: 'none',
      },
    },
  },
  container: {
    padding: '5%',
    flexGrow: 1,
  },
  title: {
    fontWeight: 600,
  },
  routeIconContainer: {
    verticalAlign: 'middle',
  },
  link: {
    cursor: 'pointer',
  },
  description: {
    marginRight: '10px',
  },
  tabs: { minHeight: '15px' },
  tab: { minHeight: '15px', minWidth: '60px' },
}));

enum EstateTaskTemplatesGridTabOption {
  ACTIVE,
  ARCHIVED,
}

const EstateTaskTemplatesGridTabs = {
  [EstateTaskTemplatesGridTabOption.ACTIVE]: 0,
  [EstateTaskTemplatesGridTabOption.ARCHIVED]: 1,
};

export default function EstateTaskTemplatesPage() {
  const classes = useStyles();
  const history = useHistory();
  const [tabSelected, setTabSelected] = useState<EstateTaskTemplatesGridTabOption>(
    EstateTaskTemplatesGridTabOption.ACTIVE,
  );
  const [showCreateEstateTaskModal, setShowCreateEstateTaskModal] = useState(false);
  const [estateTaskToEdit, setEstateTaskToEdit] = useState<EstateTask_FieldsFragment | null>(null);

  const gridApiRef = useGridApiRef();

  const [
    getAllEstateTasksForMasterTaskListDataGridLazyQuery,
    getAllEstateTasksForMasterTaskListDataGridLazyQueryResult,
  ] = useEstateTask_GetAllEstateTasksForMasterTaskListDataGridLazyQuery({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
    variables: {
      input: {
        isArchived: tabSelected === EstateTaskTemplatesGridTabOption.ACTIVE ? false : true,
        paginationInput: {
          page: defaultPaginationModel.page,
          pageSize: defaultPaginationModel.pageSize,
        },
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting all estate tasks: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const getDocumentGenerationTemplateGridToolbar = () => {
    return (
      <div>
        <GridToolbarFilterButton />
      </div>
    );
  };

  const getEstateTypeDisplay = (estateType: EstateType) => {
    let estateTypeDisplay: string;
    switch (estateType) {
      case EstateType.Truadmin:
        estateTypeDisplay = 'Truadmin';
        break;
      case EstateType.Truplanning:
        estateTypeDisplay = 'Truplanning';
        break;
      case EstateType.Truproxy:
        estateTypeDisplay = 'Truproxy';
        break;
      case EstateType.Trustadmin:
        estateTypeDisplay = 'Trustadmin';
        break;
      default:
        estateTypeDisplay = '';
        break;
    }
    return estateTypeDisplay;
  };

  const columns: GridColDef[] = [
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.SortOrder,
      filterable: true,
      minWidth: 20,
      headerName: 'Order',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <Link
              className={classes.link}
              onClick={() => {
                setEstateTaskToEdit(params.row);
              }}
            >
              <Icon name="edit-2" size={16} color="#44D2F2" />
            </Link>
            &nbsp;
            <Typography className={classes.description}>{params.row.sortOrder}</Typography>
          </>
        );
      },
      valueGetter: ({ row }) => row.sortOrder,
    },
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.EstateType,
      filterable: true,
      minWidth: 100,
      headerName: 'Type',
      renderCell: (params: GridRenderCellParams) => {
        const estateTypeDisplay = getEstateTypeDisplay(params.row.estateType);

        return (
          <>
            <Typography className={classes.description}>{estateTypeDisplay}</Typography>
          </>
        );
      },
      valueGetter: ({ row }) => getEstateTypeDisplay(row.estateType),
    },
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.Group,
      filterable: true,
      minWidth: 250,
      headerName: 'Group',
      renderCell: (params: GridRenderCellParams) => {
        const groupName = EstateTaskGroupLabel[params.row.group as EstateTaskGroup];

        return (
          <>
            <Typography className={classes.description}>{groupName}</Typography>
          </>
        );
      },
      valueGetter: ({ row }) => EstateTaskGroupLabel[row.group as EstateTaskGroup],
    },
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.Label,
      filterable: true,
      minWidth: 300,
      headerName: 'Label',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip title={params.row.label}>
            <Typography className={classes.description}>
              {truncateText(params.row.label, 30)}
            </Typography>
          </Tooltip>
        );
      },
      valueGetter: ({ row }) => row.label,
    },
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.LabelHint,
      filterable: true,
      minWidth: 200,
      headerName: 'Label Hint',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip title={params.row.labelHint}>
            <Typography className={classes.description}>
              {truncateText(params.row.labelHint, 15)}
            </Typography>
          </Tooltip>
        );
      },
      valueGetter: ({ row }) => row.labelHint,
    },
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.Price,
      filterable: false,
      minWidth: 200,
      headerName: 'Price (cents)',
      renderCell: (params: GridRenderCellParams) => {
        const priceDisplay = params.row.price && params.row.price > 0 ? params.row.price : '';
        return (
          <>
            <Typography className={classes.description}>{priceDisplay}</Typography>
          </>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.HasAssignButton,
      filterable: true,
      minWidth: 200,
      headerName: 'Has Assign Button',
      renderCell: (params: GridRenderCellParams) => {
        const hasAssignButtonDisplay = params.row.hasAssignButton ? 'Yes' : 'No';

        return (
          <>
            <Typography className={classes.description}>{hasAssignButtonDisplay}</Typography>
          </>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.AssignButtonLabel,
      filterable: true,
      minWidth: 200,
      headerName: 'Assign Button Label',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Tooltip title={params.row.assignButtonLabel}>
            <Typography className={classes.description}>
              {truncateText(params.row.assignButtonLabel, 15)}
            </Typography>
          </Tooltip>
        );
      },
      valueGetter: ({ row }) => row.assignButtonLabel,
    },
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.AllowPaymentQuantity,
      filterable: false,
      minWidth: 200,
      headerName: 'Allow Payment Qty',
      renderCell: (params: GridRenderCellParams) => {
        const allowPaymentQuantityDisplay = params.row.allowPaymentQuantity ? 'Yes' : 'No';

        return (
          <>
            <Typography className={classes.description}>{allowPaymentQuantityDisplay}</Typography>
          </>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.UserCanAssignAgain,
      filterable: true,
      minWidth: 250,
      headerName: 'User Can Assign Again',
      renderCell: (params: GridRenderCellParams) => {
        const userCanAssignAgainDisplay = params.row.userCanAssignAgain ? 'Yes' : 'No';

        return (
          <>
            <Typography className={classes.description}>{userCanAssignAgainDisplay}</Typography>
          </>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.DeadlineMonthsFromDateOfDeath,
      filterable: true,
      minWidth: 400,
      headerName: 'Deadline',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <Typography className={classes.description}>
              {params.row.deadlineType === EstateTaskDeadlineType.MonthsFromDateOfDeath
                ? `Months from Date of Death (${params.row.deadlineMonthsFromDateOfDeath} months)`
                : params.row.deadlineType === EstateTaskDeadlineType.TaxDay
                  ? 'Tax Day'
                  : ''}
            </Typography>
          </>
        );
      },
      valueGetter(params) {
        return params.row.deadlineType === EstateTaskDeadlineType.MonthsFromDateOfDeath
          ? `Months from Date of Death (${params.row.deadlineMonthsFromDateOfDeath} months)`
          : params.row.deadlineType === EstateTaskDeadlineType.TaxDay
            ? 'Tax Day'
            : '';
      },
    },
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.AutoDocumentGeneration,
      filterable: true,
      minWidth: 250,
      headerName: 'Auto Document Generation',
      renderCell: (params: GridRenderCellParams) => {
        const autoDocumentGenerationDisplay = params.row.autoDocumentGeneration ? 'Yes' : 'No';

        return (
          <>
            <Typography className={classes.description}>{autoDocumentGenerationDisplay}</Typography>
          </>
        );
      },
    },
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.DocumentGenerationTemplateName,
      filterable: true,
      minWidth: 250,
      headerName: 'Template',
      renderCell: (params: GridRenderCellParams) => {
        const documentGenerationTemplateDisplay = params.row.documentGenerationTemplate?.name || '';

        return (
          <>
            <Typography className={classes.description}>
              {documentGenerationTemplateDisplay}
            </Typography>
          </>
        );
      },
      valueGetter: ({ row }) => row.documentGenerationTemplate?.name || '',
    },
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.ConditionalTemplateQuestionSetName,
      filterable: true,
      minWidth: 250,
      headerName: 'Question Set',
      renderCell: (params: GridRenderCellParams) => {
        const conditionalTemplateQuestionSetDisplay =
          params.row.conditionalTemplateQuestionSet?.name || '';

        return (
          <>
            <Typography className={classes.description}>
              {conditionalTemplateQuestionSetDisplay}
            </Typography>
          </>
        );
      },
      valueGetter: ({ row }) => row.conditionalTemplateQuestionSet?.name || '',
    },
    {
      disableColumnMenu: true,
      field: MasterTaskListDataGridColumns.MinimumFeatureSubscriptionType,
      filterable: true,
      minWidth: 250,
      headerName: 'Min Sub Level',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <>
            <Typography className={classes.description}>
              {params.row.minimumFeatureSubscriptionType.toString()}
            </Typography>
          </>
        );
      },
      valueGetter: ({ row }) => row.minimumFeatureSubscriptionType.toString(),
    },
  ];

  const handleTabChange = (_e: ChangeEvent<any>, newTabSelected: number) => {
    switch (newTabSelected) {
      case 0:
        setTabSelected(EstateTaskTemplatesGridTabOption.ACTIVE);
        break;
      case 1:
        setTabSelected(EstateTaskTemplatesGridTabOption.ARCHIVED);
        break;
    }
  };

  useEffect(() => {
    // if the data grid filter is supposed to persist through tabs, then the filter model needs to be combined with the updated base before its sent to the server
    (async () => {
      await getQueryRefetchForDataGridQuery();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabSelected]);

  const getQueryRefetchForDataGridQuery = async () => {
    await getAllEstateTasksForMasterTaskListDataGridLazyQueryResult.refetch({
      input: {
        isArchived: tabSelected === EstateTaskTemplatesGridTabOption.ACTIVE ? false : true,
        paginationInput: {
          pageFilter: [] as EstateTask_MasterTaskListDataGridPageFilter[],
          page: defaultPaginationModel.page,
          pageSize: defaultPaginationModel.pageSize,
        },
      },
    });
  };

  if (getAllEstateTasksForMasterTaskListDataGridLazyQueryResult.loading) {
    return <Spinner />;
  }

  return (
    <HomeLayout showPortalName={false}>
      <>
        <CreateEstateTaskModal
          showModal={showCreateEstateTaskModal}
          setShowModal={() => {
            setShowCreateEstateTaskModal(false);
            getAllEstateTasksForMasterTaskListDataGridLazyQueryResult.refetch();
          }}
        />

        {estateTaskToEdit && (
          <UpdateEstateTaskModal
            showModal={Boolean(estateTaskToEdit)}
            setShowModal={() => {
              setEstateTaskToEdit(null);
              getAllEstateTasksForMasterTaskListDataGridLazyQueryResult.refetch();
            }}
            estateTask={estateTaskToEdit}
          />
        )}
        <div className={classes.container}>
          <div style={{ display: 'flex', verticalAlign: 'bottom' }}>
            <Typography className={classes.title} variant="h5" style={{ flex: 3 }}>
              <span
                onClick={() => {
                  history.push(`/home`);
                }}
              >
                <span className={classes.routeIconContainer}>
                  <img src="/home-icon.svg" alt="home-icon" />
                </span>
                <span className={classes.routeIconContainer}>
                  <ChevronRight />
                </span>
              </span>
              Master Task List
            </Typography>
            <div style={{ flex: 3 }}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                style={{ marginTop: '3px', padding: '2px 10px' }}
                onClick={() => {
                  setShowCreateEstateTaskModal(true);
                }}
              >
                + Add a New Estate Task
              </Button>
            </div>
          </div>
          <Grid container spacing={2}>
            <Grid item md={12} xs={12}>
              <div style={{ width: '100%' }}>
                <Tabs
                  value={EstateTaskTemplatesGridTabs[tabSelected]}
                  indicatorColor="primary"
                  textColor="primary"
                  onChange={handleTabChange}
                  className={classes.tabs}
                >
                  <Tab className={classes.tab} label="Active" />
                  <Tab className={classes.tab} label="Archived" />
                </Tabs>
                <ServerEnabledDataGrid
                  apiRef={gridApiRef}
                  gridStateKey={GridStateKey.MasterTaskListGrid}
                  className={classes.root}
                  loading={getAllEstateTasksForMasterTaskListDataGridLazyQueryResult.loading}
                  queryResult={getAllEstateTasksForMasterTaskListDataGridLazyQueryResult}
                  executeLazyQuery={getAllEstateTasksForMasterTaskListDataGridLazyQuery}
                  columns={columns}
                  disableColumnReorder
                  disableRowSelectionOnClick
                  autoHeight={true}
                  rowHeight={65}
                  pagination
                  slots={{
                    toolbar: getDocumentGenerationTemplateGridToolbar,
                  }}
                />
              </div>
            </Grid>
          </Grid>
        </div>
      </>
    </HomeLayout>
  );
}
